import { useEffect, useState } from 'react';
import InvoiceTable from '../tables/InvoiceTable';
import { InvoiceDTO } from '../../services/apiClient';
import { useInvoiceApi } from '../../hooks/useInvoiceApi';
import { confirmDelete, showAlert } from '../../utils/sweetAlertUtils';
import { useNavigate } from 'react-router-dom';
import { FileViewer } from '../common/FileViewer';

interface ViewInvoiceHistoryProps {
  memberId: string;
}

const ViewInvoiceHistory: React.FC<ViewInvoiceHistoryProps> = ({ memberId }) => {

  const [allInvoices, setAllInvoices] = useState<InvoiceDTO[]>([]);
  const [items, setItems] = useState<InvoiceDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const navigate = useNavigate();

  // File viewer state
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerFileName, setViewerFileName] = useState('');

  const { searchMemberInvoices, remove, getInvoiceSasUrl, resend, isLoading, error } = useInvoiceApi();

  const fetchItems = async () => {
    try {
      // Use filtered endpoint that shows only latest unpaid batch invoices (same as member dashboard)
      const response = await searchMemberInvoices(memberId);
      const invoices = response?.data?.results || [];
      setAllInvoices(invoices);
      setTotalRecords(invoices.length);
    } catch (error) {
      console.error('Error fetching Invoices:', error);
    }
  };

  // Update paginated items when page/pageSize/allInvoices changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setItems(allInvoices.slice(startIndex, endIndex));
  }, [currentPage, pageSize, allInvoices]);

  useEffect(() => {
    setCurrentPage(1);
  }, [memberId]);

  useEffect(() => {
    if (!memberId) return;
    fetchItems();
  }, [memberId]);
  const handleViewDocument = async (invoice: InvoiceDTO) => {
    try {
      const response = await getInvoiceSasUrl(invoice.id ?? '');
      const fileName = invoice.invoiceNumber
        ? `Invoice_${invoice.invoiceNumber}.pdf`
        : `PaymentNotice_${invoice.salesOrderNumber}.pdf`;

      if (response && response.success && response.data) {
        setViewerUrl(response.data);
        setViewerFileName(fileName);
        setIsViewerOpen(true);
      }
    } catch (error) {
      console.error('Error loading invoice document:', error);
    }
  };

  const handleResend = async (invoice: InvoiceDTO) => {
    try {
      const response = await resend(invoice.id ?? '');
      if (response && response.success) {
        await showAlert({
          title: 'Success',
          text: response.message || `${invoice.invoiceNumber ? 'Invoice' : 'Payment notice'} resent successfully!`,
          icon: 'success',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error resending invoice or notice:', error);
      await showAlert({
        title: 'Error',
        text: 'Failed to resend. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleDelete = async (dto: InvoiceDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting Invoice:', error);
    }
  };

  return (

    <>
      <InvoiceTable
        data={items}
        handleEdit={(dto) => navigate(`/admin/invoices/${dto.id}`)}
        handleViewDocument={handleViewDocument}
        handleResend={handleResend}
        handleDelete={handleDelete}
        currentPage={currentPage}
        totalPages={Math.ceil(totalRecords / pageSize)}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isLoading={isLoading}
      />

      {isViewerOpen && (
        <FileViewer
          fileName={viewerFileName}
          sasUrl={viewerUrl}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
    </>



  );
};

export default ViewInvoiceHistory;
